import graphql__a698b8e91a50050f41a4 from "./__generated__/ConfigurationBankTransactionRulesViewQuery.graphql.ts";
import graphql__91951a2ce6baede035bb from "./__generated__/ConfigurationBankTransactionRulesViewMoveMutation.graphql.ts";import * as React from "react";
import {startTransition, useState} from "react";
import {useOutletContext} from "react-router";
import {graphql} from "react-relay";
import {usePreloadedQuery, useQueryLoader, useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {faArrowDown, faArrowUp, faPencil, faPlusCircle, faTrash} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import EditBankTransactionRuleDialog from "./dialog/EditBankTransactionRuleDialog";
import DeleteBankTransactionRuleDialog from "./dialog/DeleteBankTransactionRuleDialog";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../contexts/ErrorContext";
import ConfigurationBankTransactionRulesViewQueryNode from "./__generated__/ConfigurationBankTransactionRulesViewQuery.graphql";
import type {ConfigurationBankTransactionRulesViewQuery as ConfigurationBankTransactionRulesViewQueryType} from "./__generated__/ConfigurationBankTransactionRulesViewQuery.graphql";
import type {ConfigurationBankTransactionRulesViewMoveMutation} from "./__generated__/ConfigurationBankTransactionRulesViewMoveMutation.graphql";

const configurationBankTransactionRulesViewQuery = graphql__a698b8e91a50050f41a4;

const moveMutation = graphql__91951a2ce6baede035bb;

type ConfigurationOutletContext = {
    bankTransactionRulesQuery: any
};

export type BankTransactionRuleRow = {
    readonly id: string
    readonly field: string
    readonly containedText: string
    readonly direction: string | null
    readonly transactionGroup: string
};

type OpenDialog =
    | {type: 'add'}
    | {type: 'edit', rule: BankTransactionRuleRow}
    | {type: 'delete', rule: BankTransactionRuleRow}
    | null;

const groupColors: Record<string, string> = {
    CUSTOMER_PAYMENTS: 'bg-green-100 text-green-800',
    SUPPLIERS: 'bg-blue-100 text-blue-800',
    SALARIES: 'bg-purple-100 text-purple-800',
    TAX_OFFICE: 'bg-yellow-100 text-yellow-800',
    FINANCING: 'bg-indigo-100 text-indigo-800',
    TRANSFER: 'bg-gray-100 text-gray-800',
    OTHER: 'bg-red-100 text-red-800',
};

/**
 * Lists the rules assigning bank bookings to groups, in the order they are tried. The first
 * matching rule wins. Changed rules apply to the next upload only.
 */
const ConfigurationBankTransactionRulesView: React.FC = () => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();
    const {bankTransactionRulesQuery} = useOutletContext<ConfigurationOutletContext>();
    const [queryRef, loadRulesQuery] = useQueryLoader<ConfigurationBankTransactionRulesViewQueryType>(ConfigurationBankTransactionRulesViewQueryNode, bankTransactionRulesQuery);
    const data = usePreloadedQuery<ConfigurationBankTransactionRulesViewQueryType>(configurationBankTransactionRulesViewQuery, queryRef ?? bankTransactionRulesQuery);
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);

    const rules = data.bankTransactionRules;

    const reloadRules = () => {
        startTransition(() => {
            loadRulesQuery({}, {fetchPolicy: 'network-only'});
        });
    };

    const closeDialog = (changed: boolean) => {
        setOpenDialog(null);
        if (changed) {
            reloadRules();
        }
    };

    const moveRule = (rule: BankTransactionRuleRow, position: number) => {
        commitMutationWithErrorHandling<ConfigurationBankTransactionRulesViewMoveMutation>(
            relayEnvironment,
            {
                mutation: moveMutation,
                variables: {input: {id: rule.id, position}},
                onCompleted: reloadRules,
                onError: reloadRules,
            },
            addError,
            {customErrorMessage: intl.formatMessage({id: 'errors.failedToMoveBankTransactionRule'})}
        );
    };

    return (
        <>
            <Panel
                headline={intl.formatMessage({id: 'view.configurationBankTransactionRules.headline'})}
                buttons={
                    <ActionButton
                        icon={faPlusCircle}
                        title={intl.formatMessage({id: 'view.configurationBankTransactionRules.addButton'})}
                        onClick={() => setOpenDialog({type: 'add'})}
                    />
                }
                fullWidthChildren={true}
            >
                <p className="px-4 py-3 sm:px-6 text-sm text-gray-500 border-b border-gray-200">
                    {intl.formatMessage({id: 'view.configurationBankTransactionRules.description'})}
                </p>
                {rules.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.configurationBankTransactionRules.empty'})}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3" textAlignment={TextAlignment.Right}>#</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.configurationBankTransactionRules.col.condition'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.configurationBankTransactionRules.col.direction'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.configurationBankTransactionRules.col.group'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {rules.map((rule, index) => (
                                    <tr key={rule.id} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                                        <TableData className="pl-4 sm:pl-6 pr-3 text-gray-500" textAlignment={TextAlignment.Right}>{rule.position}</TableData>
                                        <TableData className="px-3">
                                            {intl.formatMessage(
                                                {id: 'view.configurationBankTransactionRules.condition'},
                                                {field: intl.formatMessage({id: 'bankTransactionRuleField.' + rule.field}), text: rule.containedText, b: (chunks) => <span className="font-medium text-gray-900">{chunks}</span>}
                                            )}
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap">
                                            {rule.direction === null
                                                ? intl.formatMessage({id: 'bankTransactionDirection.BOTH'})
                                                : intl.formatMessage({id: 'bankTransactionDirection.' + rule.direction})}
                                        </TableData>
                                        <TableData className="px-3 whitespace-nowrap">
                                            <span className={'inline-flex rounded-full px-2 py-0.5 text-xs font-medium ' + (groupColors[rule.transactionGroup] ?? 'bg-gray-100 text-gray-800')}>
                                                {intl.formatMessage({id: 'bankTransactionGroup.' + rule.transactionGroup})}
                                            </span>
                                        </TableData>
                                        <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                            <ActionButton
                                                icon={faArrowUp}
                                                title={intl.formatMessage({id: 'view.configurationBankTransactionRules.action.moveUp'})}
                                                disabled={index === 0}
                                                onClick={() => moveRule(rule, rule.position - 1)}
                                            />
                                            <ActionButton
                                                icon={faArrowDown}
                                                title={intl.formatMessage({id: 'view.configurationBankTransactionRules.action.moveDown'})}
                                                disabled={index === rules.length - 1}
                                                onClick={() => moveRule(rule, rule.position + 1)}
                                            />
                                            <ActionButton
                                                icon={faPencil}
                                                title={intl.formatMessage({id: 'view.configurationBankTransactionRules.action.edit'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'edit', rule})}
                                            />
                                            <ActionButton
                                                icon={faTrash}
                                                title={intl.formatMessage({id: 'view.configurationBankTransactionRules.action.delete'})}
                                                additionalClassname="invisible group-hover:visible"
                                                onClick={() => setOpenDialog({type: 'delete', rule})}
                                            />
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>

            {openDialog?.type === 'add' && (
                <EditBankTransactionRuleDialog onClose={closeDialog}/>
            )}
            {openDialog?.type === 'edit' && (
                <EditBankTransactionRuleDialog rule={openDialog.rule} onClose={closeDialog}/>
            )}
            {openDialog?.type === 'delete' && (
                <DeleteBankTransactionRuleDialog rule={openDialog.rule} onClose={closeDialog}/>
            )}
        </>
    );
};

export default ConfigurationBankTransactionRulesView;
